import { Error as ErrorIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import React, { type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo?: ErrorInfo) => void;
  resetOnPropsChange?: boolean;
}

interface ErrorState {
  hasError: boolean;
  error: Error | null;
}

const DefaultErrorFallback = ({ error, onRetry }: { error: Error | null; onRetry: () => void }) => (
  <Box
    role="alert"
    aria-live="assertive"
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      p: 3,
      textAlign: 'center',
    }}
  >
    <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} aria-hidden="true" />
    <h2
      style={{
        fontSize: '1.5rem',
        fontWeight: 'normal',
        marginBottom: '0.35em',
        fontFamily: 'Roboto, sans-serif',
      }}
    >
      Something went wrong
    </h2>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ mb: 3, maxWidth: '500px' }}
      aria-describedby="error-details"
    >
      {error?.message || 'An unexpected error occurred while loading this component.'}
    </Typography>
    <Button
      variant="contained"
      startIcon={<RefreshIcon />}
      onClick={onRetry}
      size="large"
      type="button"
      aria-label="Retry loading the component"
    >
      Try Again
    </Button>
    <div
      id="error-details"
      style={{
        position: 'absolute',
        left: '-10000px',
        top: 'auto',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
      }}
    >
      {error?.message
        ? `Technical error: ${error.message}`
        : 'A component failed to load properly. You can try reloading to fix the issue.'}
    </div>
  </Box>
);

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    if (hasError && prevProps.children !== this.props.children) {
      if (resetOnPropsChange) {
        this.setState({ hasError: false, error: null });
      }
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      return <DefaultErrorFallback error={error} onRetry={this.handleRetry} />;
    }

    return children;
  }
}

export const useErrorHandler = () => {
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  const captureError = React.useCallback((error: Error) => {
    setError(error);
  }, []);

  return { captureError, resetError };
};

export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorFallback?: ReactNode
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary fallback={errorFallback}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
};
